import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { customersApi as C, CUST_STATUS_STYLE } from "@/lib/customers";
import { CRM_STATUS_STYLE } from "@/lib/crm";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Pencil, Power } from "lucide-react";

function Row({ label, value }) {
  return <div className="flex justify-between border-b border-slate-100 py-1.5 text-sm"><span className="text-slate-500">{label}</span><span className="font-medium text-slate-800">{value || "—"}</span></div>;
}
function fmtAddr(a) {
  if (!a) return "—";
  return [a.line1, a.line2, a.city, a.state, a.pincode].filter(Boolean).join(", ") || "—";
}

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { can } = useAuth();
  const [cust, setCust] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([C.get(id), C.enquiries(id)]);
      setCust(c); setEnquiries(e);
    } catch { toast.error("Not found"); navigate("/customers"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const toggleStatus = async () => {
    try { const c = await C.setStatus(id, cust.status === "Active" ? "Inactive" : "Active"); setCust(c); toast.success(`Marked ${c.status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  if (loading || !cust) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-indigo-600" /></div>;

  return (
    <div className="space-y-6" data-testid="customer-detail-page">
      <button onClick={() => navigate("/customers")} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="h-4 w-4" />Back to customers</button>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h2 className="font-heading text-2xl font-bold tracking-tight text-slate-900">{cust.company_name}</h2>
            <Badge className={`hover:bg-inherit ${CUST_STATUS_STYLE[cust.status]}`} data-testid="cust-status-badge">{cust.status}</Badge>
          </div>
          <p className="mt-1 text-sm text-slate-500">{cust.customer_code} · {cust.category} · {cust.gstin || "No GSTIN"}</p>
        </div>
        {can("customers", "edit") && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={toggleStatus} data-testid="toggle-status-btn"><Power className="mr-2 h-4 w-4" />{cust.status === "Active" ? "Deactivate" : "Activate"}</Button>
            <Button onClick={() => navigate(`/customers/${id}/edit`)} data-testid="edit-customer-btn" className="bg-indigo-600 text-white hover:bg-indigo-700"><Pencil className="mr-2 h-4 w-4" />Edit</Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="overview">
        <TabsList data-testid="customer-tabs">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="addresses" data-testid="tab-addresses">Addresses</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="enquiries" data-testid="tab-enquiries">Enquiries ({enquiries.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="p-6"><h3 className="mb-3 font-heading font-semibold text-slate-900">Company & Tax</h3>
              <Row label="Customer code" value={cust.customer_code} /><Row label="GSTIN" value={cust.gstin} />
              <Row label="PAN" value={cust.pan} /><Row label="Tax type" value={cust.tax_type} /><Row label="Category" value={cust.category} />
            </Card>
            <Card className="p-6"><h3 className="mb-3 font-heading font-semibold text-slate-900">Payment & Credit</h3>
              <Row label="Payment terms" value={cust.payment_terms} /><Row label="Credit days" value={cust.credit_days} />
              <Row label="Credit limit" value={cust.credit_limit ? `₹${cust.credit_limit.toLocaleString("en-IN")}` : "—"} />
            </Card>
            {cust.notes && <Card className="p-6 lg:col-span-2"><h3 className="mb-2 font-heading font-semibold text-slate-900">Notes</h3><p className="text-sm text-slate-600">{cust.notes}</p></Card>}
          </div>
        </TabsContent>

        <TabsContent value="addresses">
          <div className="grid gap-6 lg:grid-cols-2">
            <Card className="p-6"><h3 className="mb-2 font-heading font-semibold text-slate-900">Billing address</h3><p className="text-sm text-slate-600">{fmtAddr(cust.billing_address)}</p></Card>
            {(cust.shipping_addresses || []).map((a) => (
              <Card key={a.id} className="p-6" data-testid={`ship-addr-${a.id}`}><h3 className="mb-2 font-heading font-semibold text-slate-900">{a.label || "Shipping address"}</h3><p className="text-sm text-slate-600">{fmtAddr(a)}</p></Card>
            ))}
            {(!cust.shipping_addresses || cust.shipping_addresses.length === 0) && <Card className="p-6 text-sm text-slate-400">No separate shipping address.</Card>}
          </div>
        </TabsContent>

        <TabsContent value="contacts">
          <Card className="p-4">
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Name</TableHead><TableHead className="text-xs uppercase text-slate-500">Designation</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Phone</TableHead><TableHead className="text-xs uppercase text-slate-500">Email</TableHead><TableHead></TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {(cust.contacts || []).map((c) => (
                  <TableRow key={c.id} data-testid={`contact-row-${c.id}`}>
                    <TableCell className="font-medium text-slate-800">{c.name}</TableCell><TableCell className="text-slate-600">{c.designation || "—"}</TableCell>
                    <TableCell className="text-slate-600">{c.phone || "—"}</TableCell><TableCell className="text-slate-600">{c.email || "—"}</TableCell>
                    <TableCell>{c.is_primary && <Badge className="bg-indigo-50 text-indigo-700 hover:bg-indigo-50">Primary</Badge>}</TableCell>
                  </TableRow>
                ))}
                {(!cust.contacts || cust.contacts.length === 0) && <TableRow><TableCell colSpan={5} className="py-8 text-center text-slate-400">No contacts</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="enquiries">
          <Card className="p-4">
            <p className="mb-3 text-sm text-slate-500">CRM enquiries linked to this customer (associated automatically by company match).</p>
            <Table>
              <TableHeader><TableRow className="bg-slate-50">
                <TableHead className="text-xs uppercase text-slate-500">Enquiry</TableHead><TableHead className="text-xs uppercase text-slate-500">Requirement</TableHead>
                <TableHead className="text-xs uppercase text-slate-500">Source</TableHead><TableHead className="text-xs uppercase text-slate-500">Status</TableHead><TableHead className="text-xs uppercase text-slate-500">Date</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {enquiries.map((e) => (
                  <TableRow key={e.id} className="cursor-pointer" onClick={() => navigate(`/crm/${e.id}`)} data-testid={`linked-enq-${e.enquiry_no}`}>
                    <TableCell className="font-medium text-slate-800">{e.enquiry_no}</TableCell>
                    <TableCell className="text-slate-600">{e.items?.length ? `${e.items[0].fabric_type} · ${e.items[0].quantity} ${e.items[0].unit}` : "—"}</TableCell>
                    <TableCell className="text-slate-500">{e.source}</TableCell>
                    <TableCell><Badge className={`hover:bg-inherit ${CRM_STATUS_STYLE[e.status]}`}>{e.status}</Badge></TableCell>
                    <TableCell className="text-slate-500">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                  </TableRow>
                ))}
                {enquiries.length === 0 && <TableRow><TableCell colSpan={5} className="py-8 text-center text-slate-400" data-testid="no-linked-enquiries">No linked enquiries yet.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
